import { HStack, PageContainer, Spread, Surface, Text } from '@/components/Atoms'
import { Card, CardContent } from '@/components/Card'
import { PageSize } from '@/components/DataTable/PageSizeSelector'
import { PageError } from '@/components/PageError'
import { PageLoader } from '@/components/PageLoader'
import {
  useAddMassClrLineComments,
  useCancelMassClrRun,
  useMassClrLineComments,
  useMassClrRun,
  useMassClrRunLines,
  usePatchMassClrLines,
  type MassClrLine,
  type MassClrLineComment,
} from '@/hooks/queries/credit/useMassClr'
import { isMassClrRunInChain, type ClrSelectableRiskClassCode } from '@repo/constants'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Separator,
  Tabs,
  TabsList,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@repo/ui/components'
import { RowSelectionState } from '@tanstack/react-table'
import { createFileRoute, Link } from '@tanstack/react-router'
import {
  ArrowLeftIcon,
  BanIcon,
  CheckCircle2Icon,
  ClockIcon,
  DownloadIcon,
  EyeIcon,
  InfoIcon,
  MessageSquareDashedIcon,
  MessageSquareTextIcon,
  MinusIcon,
  MoreHorizontalIcon,
  PencilIcon,
  PowerOffIcon,
  SendIcon,
  Trash2Icon,
  TrendingDownIcon,
  UndoIcon,
  XCircleIcon,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { DeviateBulkDialog } from '../-components/DeviateBulkDialog'
import { LineCommentDialog } from '../-components/LineCommentDialog'
import { MassClrBulkBar } from '../-components/MassClrBulkBar'
import { MassClrChainReview } from '../-components/MassClrChainReview'
import { MassClrFilterTab } from '../-components/MassClrFilterTab'
import { MassClrLineSheet } from '../-components/MassClrLineSheet'
import { MassClrLinesTable, MassClrLineRow } from '../-components/MassClrLinesTable'
import { MassClrReceipt } from '../-components/MassClrReceipt'
import { MassRunStatusBadge } from '../-components/MassRunStatusBadge'
import { RestoreLineDialog } from '../-components/RestoreLineDialog'
import { downloadMassClrLinesCsv } from '../-components/massClrFormat'
import { massClrRunSearchSchema } from '../-data/runSearch'
import { useMassClrLineFilters } from '../-data/useMassClrLineFilters'
import {
  computeRunStats,
  currentCycleCommentedLineIds,
  gateCommentedLineIds,
  getMassClrLineState,
  MASS_CLR_LINE_FILTERS,
  matchesLineFilter,
  preparerCommentedLineIds,
  type MassClrLineFilter,
} from '../-data/lineState'

export const Route = createFileRoute('/_auth/_modules/credit/credit-limit-reviews/mass/$runId/')({
  validateSearch: massClrRunSearchSchema,
  component: RouteComponent,
})

const FILTER_ICONS: Record<MassClrLineFilter, React.ComponentType<{ className?: string }>> = {
  all: InfoIcon,
  flagged: EyeIcon,
  against_trend: TrendingDownIcon,
  unchanged: MinusIcon,
  deviated: PencilIcon,
  clean: CheckCircle2Icon,
  inactive: PowerOffIcon,
  commented: MessageSquareTextIcon,
  uncommented: MessageSquareDashedIcon,
  withdrawn: InfoIcon,
  excluded: BanIcon,
  removed: Trash2Icon,
}

// `level-1` is the card white: the page itself is a `level-2` surface, so a `level-2` box here
// would show nothing but its border.
const FILTER_TONES: Record<MassClrLineFilter, 'warning' | 'info' | 'level-1'> = {
  all: 'level-1',
  flagged: 'warning',
  against_trend: 'warning',
  unchanged: 'info',
  deviated: 'info',
  clean: 'level-1',
  inactive: 'info',
  commented: 'warning',
  uncommented: 'level-1',
  withdrawn: 'level-1',
  excluded: 'level-1',
  removed: 'level-1',
}

function RouteComponent() {
  const { t } = useTranslation(['credit', 'common'])
  const params = Route.useParams()
  const search = Route.useSearch()
  const runId = Number(params.runId)
  const navigate = Route.useNavigate()

  const runQuery = useMassClrRun(runId)
  const run = runQuery.data
  // Three readings of one page: the OWNER of an editable run gets the workbench; the holder of
  // the pending slot gets the chain review (full table + comments + decisions, FUZE-616); anyone
  // else gets the receipt.
  const isEditable = run?.status === 'draft' || run?.status === 'sent_back_to_preparer'
  const isPreparerMode = isEditable && (run?.viewer.isPreparer ?? false)
  const linesQuery = useMassClrRunLines(runId, isPreparerMode)
  // The preparer's own notes for the approver, and — after a send-back — the approver's flags.
  const commentsQuery = useMassClrLineComments(runId, isPreparerMode)

  const comments = commentsQuery.data?.comments ?? []
  const commentsByLine = new Map<number, MassClrLineComment[]>()
  for (const comment of comments) {
    const thread = commentsByLine.get(comment.lineId)
    if (thread) thread.push(comment)
    else commentsByLine.set(comment.lineId, [comment])
  }
  const commentedLineIds = currentCycleCommentedLineIds(comments, run?.actions ?? [])
  // The approver's flags alone: what the send-back banner counts.
  const approverCommentedLineIds = gateCommentedLineIds(comments, run?.actions ?? [])

  const [bucket, setBucket] = useState<MassClrLineFilter>(search.bucket ?? 'all')
  const [keyword, setKeyword] = useState('')
  // A draft has never reached an approver, so it has no approver comments to show or filter on.
  const lineFilters = useMassClrLineFilters(
    runId,
    isPreparerMode,
    comments,
    run?.status !== 'draft',
  )
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState<PageSize>(30)
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [editingLineId, setEditingLineId] = useState<number | null>(null)
  const [isDeviateOpen, setIsDeviateOpen] = useState(false)
  const [isCancelOpen, setIsCancelOpen] = useState(false)
  const [commentTargetIds, setCommentTargetIds] = useState<number[] | null>(null)
  const [restoreTargetIds, setRestoreTargetIds] = useState<number[] | null>(null)

  const patchMutation = usePatchMassClrLines(runId)
  const commentMutation = useAddMassClrLineComments(runId)
  const cancelMutation = useCancelMassClrRun()

  // A bucket, search or filter change re-anchors the view: page 1, nothing selected.
  useEffect(() => {
    setPage(1)
    setRowSelection({})
  }, [bucket, keyword, lineFilters.filtersKey])

  if (runQuery.isPending) return <PageLoader />
  if (!run) return <PageError />
  if (!isPreparerMode) {
    if (run.viewer.canAct && isMassClrRunInChain(run.status)) {
      return (
        <PageContainer maxWidth="wide">
          <MassClrChainReview run={run} initialBucket={search.bucket} />
        </PageContainer>
      )
    }
    return <MassClrReceipt run={run} />
  }

  const lines = linesQuery.data?.lines ?? []
  const hasComments = commentedLineIds.size > 0

  const stats = computeRunStats(lines, commentedLineIds)
  // The submit gate, mirrored from the Review & Submit page and the server: a note of yours on
  // every line in the run.
  const preparerUncommented = computeRunStats(lines, preparerCommentedLineIds(comments)).counts
    .uncommented

  const query = keyword.trim().toLowerCase()
  const filteredLines = lines.filter((line) => {
    if (!matchesLineFilter(line, bucket, commentedLineIds)) return false
    if (!lineFilters.matchesLineFilters(line)) return false
    if (!query) return true
    return line.erp.includes(query) || line.businessName.toLowerCase().includes(query)
  })

  const safePage = Math.min(page, Math.max(1, Math.ceil(filteredLines.length / pageSize)))
  const pageRows: MassClrLineRow[] = filteredLines
    .slice((safePage - 1) * pageSize, safePage * pageSize)
    .map((line) => {
      const lineState = getMassClrLineState(line)
      return {
        ...line,
        lineState,
        rowStatus: lineState.bucket === 'deviated' ? ('critical' as const) : ('default' as const),
      }
    })

  const selectedIds = Object.entries(rowSelection)
    .filter(([, isSelected]) => isSelected)
    .map(([id]) => Number(id))
  const areAllSelected = selectedIds.length >= filteredLines.length && filteredLines.length > 0

  const clearSelection = () => setRowSelection({})

  const toggleSelectAll = () => {
    if (areAllSelected) {
      clearSelection()
      return
    }
    setRowSelection(Object.fromEntries(filteredLines.map((line) => [String(line.id), true])))
  }

  const exportCsv = (onlySelected: boolean) => {
    const exported = onlySelected
      ? filteredLines.filter((line) => rowSelection[String(line.id)])
      : filteredLines
    const count = downloadMassClrLinesCsv(`${run.reference}-${run.id}-lines.csv`, exported, {
      multiplierGrid: run.multiplierGrid,
      commentsByLine,
    })
    toast.success(t('massClr.toasts.exported', { count }))
  }

  const editingLine: MassClrLine | null =
    filteredLines.find((line) => line.id === editingLineId) ?? null
  const stepEditingLine = (direction: 1 | -1) => {
    if (editingLineId === null || filteredLines.length === 0) return
    const index = filteredLines.findIndex((line) => line.id === editingLineId)
    const next = filteredLines[(index + direction + filteredLines.length) % filteredLines.length]
    if (next) setEditingLineId(next.id)
  }

  const visibleFilters = MASS_CLR_LINE_FILTERS.filter((filter) => {
    if (filter === 'withdrawn') return stats.counts.withdrawn > 0
    if (filter === 'removed') return stats.counts.removed > 0
    if (filter === 'excluded') return stats.counts.excluded > 0
    if (filter === 'inactive') return stats.counts.inactive > 0
    // The comment axis only exists once someone wrote a note — yours, or the approver's.
    if (filter === 'commented' || filter === 'uncommented') return hasComments
    return true
  })

  // The approver's container note on the latest send-back — the run-level context for the
  // flagged lines below. `startsWith`: runs from before the reviewer slot went carry a
  // `sent_back_to_reviewer` row as their latest send-back.
  const sendBackAction =
    run.status === 'sent_back_to_preparer'
      ? [...run.actions].reverse().find((action) => action.action.startsWith('sent_back'))
      : undefined
  const sendBackActorName = sendBackAction?.actorName ?? t('delegationOfAuthority.slot.approver')

  const openCommentDialog = (lineIds: number[]) => setCommentTargetIds(lineIds)
  const applyComment = (comment: string) => {
    if (!commentTargetIds) return
    commentMutation.mutate(
      { lineIds: commentTargetIds, comment },
      { onSuccess: () => setCommentTargetIds(null) },
    )
    clearSelection()
  }

  const FilterIcon = FILTER_ICONS[bucket]

  return (
    <PageContainer maxWidth="wide">
      {/* Sticky within the app's main scroll area: header row AND the bucket tabs travel
          together. Negative margins cancel the PageContainer padding so the block spans edge to
          edge and its background hides rows scrolling under. */}
      <div className="bg-background-level-2 sticky top-0 z-20 -mx-8 -mt-8 space-y-3 border-b px-8 py-3">
        <Spread align="center" className="flex-wrap gap-3">
          <HStack gap="sm" align="center" className="min-w-0">
            <Button type="button" variant="ghost" size="sm" asChild>
              <Link to="/credit/credit-limit-reviews/mass">
                <ArrowLeftIcon />
                {t('massClr.pageHeader')}
              </Link>
            </Button>
            <Separator orientation="vertical" className="h-5" />
            <Text weight="medium" className="truncate">
              {run.name}
            </Text>
            <MassRunStatusBadge status={run.status} pendingSlot={run.pendingSlot} />
            {/* items-center flex keeps the clock optically on the text's centerline — same trick
              the Badge component uses internally for its icons. */}
            <Text
              size="xs"
              tone="muted"
              as="span"
              className="hidden items-center gap-1.5 whitespace-nowrap lg:inline-flex"
            >
              <ClockIcon className="size-3.5 shrink-0" />
              {t('massClr.run.savedAutomatically')}
            </Text>
          </HStack>
          {/* Same shrink treatment as the chain header: the cluster wraps rather than overflowing
              the page, and the counter takes its own line first. */}
          <HStack gap="sm" align="center" className="min-w-0 flex-wrap justify-end">
            {preparerUncommented > 0 && (
              <Text
                size="xs"
                weight="medium"
                tone="warning-dark"
                as="span"
                className="basis-full whitespace-nowrap text-right"
              >
                {t('massClr.review.uncommentedCounter', { count: preparerUncommented })}
              </Text>
            )}
            {/* A disabled button swallows pointer events, so the span carries the hover. */}
            {preparerUncommented > 0 ? (
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex">
                      <Button type="button" size="sm" disabled>
                        <SendIcon />
                        {t('massClr.run.reviewAndSubmit')}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" align="end" className="max-w-xs text-pretty">
                    {t('massClr.submit.uncommentedWarning', { count: preparerUncommented })}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <Button type="button" size="sm" asChild>
                <Link
                  to="/credit/credit-limit-reviews/mass/$runId/submit"
                  params={{ runId: params.runId }}
                >
                  <SendIcon />
                  {t('massClr.run.reviewAndSubmit')}
                </Link>
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="secondary" size="icon-sm">
                  <MoreHorizontalIcon />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-52">
                <DropdownMenuItem onClick={() => exportCsv(false)}>
                  <DownloadIcon />
                  {t('massClr.run.menu.export')}
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/credit/credit-limit-reviews/mass">
                    <CheckCircle2Icon />
                    {t('massClr.run.menu.saveExit')}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-critical-text-dark"
                  onClick={() => setIsCancelOpen(true)}
                >
                  <XCircleIcon />
                  {t('massClr.run.menu.cancel')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </HStack>
        </Spread>

        <Tabs value={bucket} onValueChange={(value) => setBucket(value as MassClrLineFilter)}>
          <TabsList className="h-auto flex-wrap justify-start">
            {visibleFilters.map((filter) => (
              <MassClrFilterTab key={filter} filter={filter} count={stats.counts[filter]} />
            ))}
          </TabsList>
        </Tabs>
      </div>

      {sendBackAction && (
        <Surface tone="warning" radius="md" className="flex w-full items-start gap-2.5 border p-3">
          <UndoIcon className="mt-0.5 size-4 shrink-0" />
          <div className="min-w-0 flex-1">
            <Text size="xs" weight="medium" className="text-pretty">
              {approverCommentedLineIds.size > 0
                ? t('massClr.run.sentBackBanner', {
                    name: sendBackActorName,
                    count: approverCommentedLineIds.size,
                  })
                : t('massClr.run.sentBackBannerNoComments', { name: sendBackActorName })}
            </Text>
            {sendBackAction.comment && (
              <Text size="xs" tone="muted" className="mt-1 text-pretty">
                “{sendBackAction.comment}”
              </Text>
            )}
          </div>
          {approverCommentedLineIds.size > 0 && bucket !== 'commented' && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setBucket('commented')}
            >
              {t('massClr.run.showThem')}
            </Button>
          )}
        </Surface>
      )}

      {stats.counts.withdrawn > 0 && bucket !== 'withdrawn' && (
        <Surface tone="level-1" radius="md" className="flex w-full items-center gap-2.5 border p-3">
          <InfoIcon className="text-muted-foreground size-4 shrink-0" />
          <Text size="xs" className="flex-1 text-pretty">
            {t('massClr.run.withdrawnBanner', { count: stats.counts.withdrawn })}
          </Text>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setBucket('withdrawn')}
          >
            {t('massClr.run.showThem')}
          </Button>
        </Surface>
      )}

      {bucket !== 'all' && (
        <Surface
          tone={FILTER_TONES[bucket]}
          radius="md"
          className="flex w-full items-start gap-2.5 border p-3"
        >
          <FilterIcon className="mt-0.5 size-4 shrink-0" />
          <Text size="xs" className="text-pretty">
            {t(`massClr.filterDesc.${bucket}`)}
          </Text>
        </Surface>
      )}

      <Card>
        <CardContent className="pt-6">
          <MassClrLinesTable
            multiplierGrid={run.multiplierGrid}
            rows={pageRows}
            rowCount={filteredLines.length}
            page={safePage}
            pageSize={pageSize}
            isPending={linesQuery.isPending || lineFilters.isPending}
            search={{
              value: keyword,
              setValue: setKeyword,
              placeholder: t('massClr.run.searchPlaceholder'),
              className: 'bg-background',
            }}
            rowSelection={rowSelection}
            comments={commentsByLine}
            showApproverComments={run.status !== 'draft'}
            filters={lineFilters.filters}
            appliedFilters={lineFilters.appliedFilters}
            queryParams={lineFilters.queryParams}
            onShowResults={lineFilters.showResults}
            onResetAllFilters={lineFilters.resetAllFilters}
            contentSlot={
              selectedIds.length > 0 ? (
                <MassClrBulkBar
                  selectedCount={selectedIds.length}
                  totalCount={filteredLines.length}
                  areAllSelected={areAllSelected}
                  onToggleSelectAll={toggleSelectAll}
                  onDeviate={() => setIsDeviateOpen(true)}
                  onComment={() => openCommentDialog(selectedIds)}
                  onExportCsv={() => exportCsv(true)}
                  onRemove={() => {
                    patchMutation.mutate({ lineIds: selectedIds, patch: { kind: 'remove' } })
                    clearSelection()
                  }}
                />
              ) : undefined
            }
            toolbarActions={
              <Button type="button" variant="secondary" size="sm" onClick={() => exportCsv(false)}>
                <DownloadIcon />
                {t('massClr.bulk.exportCsv')}
              </Button>
            }
            onRowSelectionChange={setRowSelection}
            onPageChange={(pageIndex) => setPage(pageIndex + 1)}
            onPageSizeChange={setPageSize}
            onCommentLine={(line) => openCommentDialog([line.id])}
            onEditLine={(line) => setEditingLineId(line.id)}
            onRemoveLine={(lineId) =>
              patchMutation.mutate({ lineIds: [lineId], patch: { kind: 'remove' } })
            }
            onRestoreLine={(line) => setRestoreTargetIds([line.id])}
          />
        </CardContent>
      </Card>

      <MassClrLineSheet
        multiplierGrid={run.multiplierGrid}
        line={editingLine}
        position={
          editingLine
            ? {
                index: filteredLines.findIndex((line) => line.id === editingLine.id) + 1,
                total: filteredLines.length,
              }
            : null
        }
        onStep={stepEditingLine}
        onClose={() => setEditingLineId(null)}
        onSave={(values) => {
          if (editingLine) {
            patchMutation.mutate({
              lineIds: [editingLine.id],
              patch: { kind: 'deviate', ...values },
            })
          }
        }}
        onReset={() => {
          if (editingLine) {
            patchMutation.mutate({ lineIds: [editingLine.id], patch: { kind: 'reset' } })
          }
        }}
        onRemove={() => {
          if (editingLine) {
            patchMutation.mutate({ lineIds: [editingLine.id], patch: { kind: 'remove' } })
          }
        }}
      />

      <DeviateBulkDialog
        isOpen={isDeviateOpen}
        selectedCount={selectedIds.length}
        onOpenChange={setIsDeviateOpen}
        onApply={(values: {
          proposedCreditLimit?: number
          proposedRiskClass?: ClrSelectableRiskClassCode
          reasonForDeviation: string
        }) => {
          patchMutation.mutate({ lineIds: selectedIds, patch: { kind: 'deviate', ...values } })
          clearSelection()
        }}
      />

      <LineCommentDialog
        targetIds={commentTargetIds}
        isPending={commentMutation.isPending}
        onClose={() => setCommentTargetIds(null)}
        onApply={applyComment}
      />

      <RestoreLineDialog
        targetIds={restoreTargetIds}
        isPending={patchMutation.isPending}
        onClose={() => setRestoreTargetIds(null)}
        onConfirm={() => {
          if (!restoreTargetIds) return
          patchMutation.mutate(
            { lineIds: restoreTargetIds, patch: { kind: 'restore' } },
            { onSuccess: () => setRestoreTargetIds(null) },
          )
        }}
      />

      <Dialog open={isCancelOpen} onOpenChange={setIsCancelOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('massClr.cancelDialog.title', { name: run.name })}</DialogTitle>
            <DialogDescription>
              {t('massClr.cancelDialog.description', { count: stats.inRun })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => setIsCancelOpen(false)}>
              {t('massClr.cancelDialog.keep')}
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                setIsCancelOpen(false)
                cancelMutation.mutate(runId, {
                  onSuccess: () => navigate({ to: '/credit/credit-limit-reviews/mass' }),
                })
              }}
            >
              {t('massClr.cancelDialog.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  )
}
